import type { CSSProperties } from "react";
import { useEffect } from "react";
import { useLocation } from "react-router";
import { styled } from "styled-components";

import { useDispatch } from "common/utilities/react-redux";
import { useGo } from "common/utilities/useGo";
import { useTrack } from "common/utilities/useTrack";
import { useTranslate } from "common/utilities/useTranslate";
import { covers } from "domains/resources/covers";
import advancedFeatures from "domains/resources/data/advancedFeatures.json";
import getStarted from "domains/resources/data/getStarted.json";
import introduction from "domains/resources/data/introduction.json";
import type { SourceVideo } from "domains/resources/types";
import { VideoCover } from "domains/resources/VideoCover";
import { setVisualMediaPlayer } from "domains/visualMediaPlayer/actions";
import { MediaType } from "domains/visualMediaPlayer/types";

const StyledVideoCard = styled.div`
  background-color: ${({ theme }) => theme.colors.backgroundSecondary};
  border-radius: 10px;
  color: ${({ theme }) => theme.colors.textBright};
  cursor: pointer;
  overflow: hidden;
`;

const StyledLabel = styled.div`
  color: ${({ theme }) => theme.colors.textFade};
  font-size: 18px;
  font-weight: bold;
  padding: 25px 20px 8px;
  text-transform: uppercase;
`;

const StyledTitle = styled.div`
  font-size: 16px;
  font-weight: bold;
  padding: 0 20px 20px;
`;

const videos = [introduction, ...getStarted, ...advancedFeatures];

export const VideoCard = ({ url, name, image, length, ...rest }: SourceVideo & { style?: CSSProperties }) => {
  const dispatch = useDispatch();
  const go = useGo();
  const { pathname, search } = useLocation();
  const track = useTrack();
  const translate = useTranslate();

  const videoUrlParam = new URLSearchParams(search).get("video");

  useEffect(() => {
    if (videoUrlParam !== name) return;
    const index = videos.findIndex((item) => item.url === url);
    dispatch(
      setVisualMediaPlayer({
        id: name,
        mediaType: MediaType.videoViaEmbeddedHtmlPlayer,
        name,
        onCloseRoute: pathname,
        splash: {
          next: index < videos.length - 1 ? videos[index + 1] : undefined,
          previous: index > 0 ? videos[index - 1] : undefined,
        },
        url,
        useReactPlayer: true,
      }),
    );
  }, [dispatch, go, pathname, name, translate, url, videoUrlParam]);

  const onClick = () => {
    track({ name: "Play Media", page: "Resources", entityName: name, entityId: url });
    go(`${pathname}?video=${name}`);
  };

  return (
    <StyledVideoCard {...rest} onClick={onClick}>
      <VideoCover image={covers[image as keyof typeof covers]} videoLength={length} height="175px" />
      <StyledLabel>{translate("app.resources.label.video")}</StyledLabel>
      <StyledTitle>{name}</StyledTitle>
    </StyledVideoCard>
  );
};
